import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { deleteChartTag, editTagsView, onPreviewChange } from "store/thunk/charts";

import { CHART_EDIT_TAGS_TABLE_STRINGS, ChartStrings } from "constants/constantStrings";

import AgTable from "components/AgTable";
import BadgeLabel from "components/BadgeLabel";
import DupontButton from "components/DupontButton";

import { TagColorOptions } from "../../../constants/chartConstant";

import { getAlarmsTableColDef } from "./ChartTags.table.config";

import "./index.scss";

const ChartTags = props => {
  const dispatch = useDispatch();
  const { EditOrignalTags } = useSelector(state => state.chartData);

  const getAvailableColors = rows => {
    rows = rows || updatedRowData;
    const usedColors = rows.map(tag => tag.Color);
    return TagColorOptions.filter(color => !usedColors.includes(color.code));
  };
  const [updatedRowData, setUpdatedRowData] = useState(EditOrignalTags);
  const [isSaveBtnDisabled, setIsSaveBtnDisabled] = useState(true);
  const [isManualRowSelection, setIsManualRowSelection] = useState(false);
  const [agGridApi, setAgGridApi] = useState();
  const [errorMessage, setErrorMessage] = useState(null);
  let gridAPI;

  useEffect(() => {
    if (EditOrignalTags.length) {
      selectDefaultRows(agGridApi);
    } else {
      onCancel();
    }
  }, [EditOrignalTags,agGridApi]);

  const valueComparator = (newRow, oldRow) =>
    newRow.Color !== oldRow.Color ||
    newRow.Type !== oldRow.Type ||
    newRow.Show !== oldRow.Show ||
    newRow.Trendline !== oldRow.Trendline ||
    newRow.Ylower !== oldRow.Ylower ||
    newRow.Yupper !== oldRow.Yupper;

  const getItemById = (TagId, items) => items.find(item => item.TagId === TagId);
  const checkLimitConditions = rows => {
    return rows.some(row => 
      row.Yupper !== null && row.Ylower !== null && row.Yupper <= row.Ylower
    );  
  };

  const validateData = (rows, isRowUpdated) => {
    setUpdatedRowData(rows);
    const isEdited = rows.some((row, idx) => {
      let isUpdated = isRowUpdated || row.isEdited;
      if (isUpdated) {
        const tableTag = rows[idx];
        isUpdated = valueComparator(tableTag, getItemById(tableTag.TagId, EditOrignalTags));
      }
      return isUpdated;
    });
    if (checkLimitConditions(rows)) {
      setErrorMessage(ChartStrings.limitsErrorMsg);
      setIsSaveBtnDisabled(true);
    } else {
      setErrorMessage(null);
      setIsSaveBtnDisabled(!isEdited);
    }
  };

  const onSelectionChanged = () => {
    if (isManualRowSelection) {
      const updatedRows = getTableData();
      dispatch(onPreviewChange(updatedRows));
    }
  };

  const getColorOptions = () => {
    const rows = getTableData();
    return getAvailableColors(rows);
  };

  const refreshColumn = col => {
    if (col === "Color") {
      gridAPI = gridAPI || agGridApi;
      const params = {
        force: true,
        suppressFlash: false,
        enableCellChangeFlash: false,
        columns: [col]
      };
      setTimeout(() => gridAPI.refreshCells(params));
    }
  };

  const updateChartView = col => {
    const rows = getTableData();
    validateData(rows);
    dispatch(onPreviewChange(rows));
    refreshColumn(col);
  };
  const tagDeleteHandler = data => {
    setIsManualRowSelection(false);
    dispatch(deleteChartTag(data));
  };

  const getTableData = () => {
    const updatedTags = [];
    gridAPI = gridAPI || agGridApi;
    if (gridAPI) gridAPI.forEachNode(node => updatedTags.push(node.data));
    return updatedTags;
  };

  const tagTypeChangeHandler = (rowIndex, data) => {
    const tempData = [...props.rowData];
    tempData[rowIndex] = data;
    dispatch(onPreviewChange(tempData));
  };

  const onCancel = () => dispatch(editTagsView({}));
  const onSave = () => {
    const updatedTags = getTableData();
    dispatch(editTagsView({ updatedTags }));
  };

  const selectDefaultRows = gridAPI => {
    if (gridAPI) {
      gridAPI.forEachNode(node => node.setSelected(node.data.Show));
      setTimeout(() => setIsManualRowSelection(true)); // Intentionally need this to update flag
    }
  };

  const onGridReady = useCallback(params => {
    gridAPI = params.api;
    setAgGridApi(gridAPI);
    selectDefaultRows(gridAPI);
    gridAPI.addGlobalListener(type => {
      if (type === "rowSelected") {
        validateData(getTableData(), true);
      }
    });
  }, []);

  const onRowSelected = event => {
    const { node } = event;
    const Show = node.isSelected();
    const updatedRowData = { ...event.data, Show, isEdited: isManualRowSelection };
    node.setData(updatedRowData);
  };

  return (
    <>
      <div className="chart-tag-container whiteBackground chartBottomContainer">
        <div className="chartBottomTopRow">
          <div className="calculatedValueText d-flex">
            <BadgeLabel
              badgeDescription={CHART_EDIT_TAGS_TABLE_STRINGS.editTags}
              badgeText={CHART_EDIT_TAGS_TABLE_STRINGS.tagsAdded.replace("[%]", EditOrignalTags.length)}
            />
            {errorMessage && <div className="chart-error-message">{errorMessage}</div>}
          </div>
          <div className="d-flex">
            <DupontButton type="secondary" onClick={onCancel} customClass="chart-tag--btn-spacing">
              {ChartStrings.cancel}
            </DupontButton>
            <DupontButton type="primary" onClick={onSave} disabled={isSaveBtnDisabled}>
              {ChartStrings.save}
            </DupontButton>
          </div>
        </div>
        <div className="fullWidth height--100">
          <AgTable
            rowData={EditOrignalTags}
            columnDefs={getAlarmsTableColDef(getColorOptions)}
            context={{ updateChartView, tagTypeChangeHandler, tagDeleteHandler }}
            onSelectionChanged={onSelectionChanged}
            onGridReady={onGridReady}
            onRowSelected={onRowSelected}
            customHeight="180px"
          />
        </div>
      </div>
    </>
  );
};

export default ChartTags;
